import React, { useState } from 'react';
import './ScheduleChangeModal.css';

/**
 * Modal pour planifier une modification de signature ou template
 */
function ScheduleChangeModal({
    isOpen,
    onClose,
    onSchedule,
    entityType = 'signature', // 'signature' ou 'template'
    entityId,
    entityName,
    currentGridData
}) {
    const [scheduledAt, setScheduledAt] = useState('');
    const [description, setDescription] = useState('');
    const [changeType, setChangeType] = useState('update');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Générer la date/heure minimum (maintenant + 1 minute)
    const getMinDateTime = () => {
        const now = new Date();
        now.setMinutes(now.getMinutes() + 1);
        return now.toISOString().slice(0, 16);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Validation
        if (!scheduledAt) {
            setError('La date de planification est requise');
            return;
        }

        const scheduledDate = new Date(scheduledAt);
        const now = new Date();

        if (scheduledDate <= now) {
            setError('La date doit être dans le futur');
            return;
        }

        setLoading(true);

        try {
            // Préparer les données
            const data = {
                entity_type: entityType,
                entity_id: entityId,
                change_type: changeType,
                scheduled_at: scheduledAt,
                description: description || null,
                new_grid_data: currentGridData // Sera encodé en JSON par le backend
            };

            await onSchedule(data);

            // Réinitialiser et fermer
            setScheduledAt('');
            setDescription('');
            setChangeType('update');
            onClose();
        } catch (err) {
            setError(err.message || 'Erreur lors de la planification');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content schedule-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Planifier une modification</h2>
                    <button className="modal-close" onClick={onClose}>&times;</button>
                </div>

                <form onSubmit={handleSubmit} className="modal-body">
                    <div className="form-group">
                        <label className="form-label">
                            {entityType === 'signature' ? 'Signature' : 'Modèle'}
                        </label>
                        <input
                            type="text"
                            value={entityName}
                            disabled
                            className="form-input"
                        />
                    </div>

                    <div className="form-group">
                        <label className="form-label">
                            Type de modification
                        </label>
                        <select
                            value={changeType}
                            onChange={(e) => setChangeType(e.target.value)}
                            className="form-select"
                        >
                            <option value="update">Mise à jour</option>
                            {entityType === 'template' && (
                                <option value="cascade_update">Mise à jour en cascade</option>
                            )}
                        </select>
                        <small className="form-help">
                            {changeType === 'update' && 'Mettre à jour l\'état actuel'}
                            {changeType === 'cascade_update' && 'Mettre à jour toutes les signatures utilisant ce modèle'}
                        </small>
                    </div>

                    <div className="form-group">
                        <label className="form-label">
                            Date et heure de planification *
                        </label>
                        <input
                            type="datetime-local"
                            value={scheduledAt}
                            onChange={(e) => setScheduledAt(e.target.value)}
                            min={getMinDateTime()}
                            required
                            className="form-input"
                        />
                        <small className="form-help">
                            La modification sera appliquée automatiquement à cette date
                        </small>
                    </div>

                    <div className="form-group">
                        <label className="form-label">
                            Description (optionnelle)
                        </label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Ex: Bannière Noël 2024"
                            rows={3}
                            className="form-textarea"
                        />
                        <small className="form-help">
                            Pour vous aider à identifier cette modification plus tard
                        </small>
                    </div>

                    {error && (
                        <div className="error-message">
                            {error}
                        </div>
                    )}

                    <div className="modal-footer">
                        <button
                            type="button"
                            onClick={onClose}
                            className="btn btn-secondary"
                            disabled={loading}
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                        >
                            {loading ? 'Planification...' : 'Planifier'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default ScheduleChangeModal;
